import { useEffect, useMemo } from "react";
import { Row, Col, Empty, Spin, Divider, Typography, Tag, Space, Flex } from "antd";
import { useSelector, useDispatch } from "react-redux";
import DocumentCard from "../../../components/CustomCard/DocumentCard";
import { document as fetchDocuments } from "../../../redux/slices/DocumentSlice";
import { DESC_TXT } from "../../../constants/constants";

const { Text, Title } = Typography;

const REQUIRED_FINANCIAL_DOCS = [
  {
    docMasterId: "DMS1000048",
    docMasterName: "financialEvaluation",
  },
  {
    docMasterId: "DMS1000046",
    docMasterName: "others",
  },
];

// 1. KYC Documents
const KYC_DOC_MASTER_IDS = new Set([
  "DMS1000001", // PAN Card
  "DMS1000002", // GST Certificate
  "DMS1000003", // TAN Certificate
  "DMS1000005", // Aadhaar Card
  "DMS1000006", // Driving Licence
  "DMS1000007", // Electricity Bill
  "DMS1000008", // Passport
  "DMS1000009", // Selfie Photograph
  "DMS1000011", // Mobile Verification
  "DMS1000012", // Email Verification
  "DMS1000016", // Director Photograph
  "DMS1000022", // Director Photograph
  "DMS1000023", // Director PAN
  "DMS1000024", // Director Aadhaar
  "DMS1000025", // PAN Document
  "DMS1000026", // PAN Card
  "DMS1000027", // GSTIN Number
  "DMS1000028", // GST Certificate
  "DMS1000029", // Director PAN
  "DMS1000030", // Director Aadhaar
  "DMS1000040", // KYC – Authorized Signatory
  "DMS1000051", // Voter ID
  "DMS1000052", // Passport (Back Side)
  "DMS1000053", // Driving Licence (Back Side)
  "DMS1000055", // Voter ID (Back Side)
  "DMS1000056", // Company PAN Card
]);

// 2. Onboarding / Financial Documents
const ONBOARDING_FINANCIAL_DOC_IDS = new Set([
  "DMS1000004", // Cancelled Cheque
  "DMS1000010", // CIN Certificate
  "DMS1000013", // Bank Account Proof
  "DMS1000014", // Enterprise Logo
  "DMS1000015", // Business Logo
  "DMS1000017", // Sanction Documents
  "DMS1000018", // Board Resolution
  "DMS1000019", // Bank Statement
  "DMS1000020", // Audited Financial Statement
  "DMS1000032", // Bank Statement (Last 12 Months)
  "DMS1000033", // Audited Financial Statements (Last 3 Years)
  "DMS1000034", // Sanction Letter (Existing Borrowings)
  "DMS1000035", // GST Returns (Last 12 Months)
  "DMS1000036", // Financial Summary
  "DMS1000037", // Provisional Financial Statement (Latest Year)
  "DMS1000038", // ITR (Last 2 Years)
  "DMS1000039", // Certificate of Incorporation
  "DMS1000041", // Current Shareholding Pattern
  "DMS1000042", // Memorandum & Articles of Association
  "DMS1000043", // Constitution Documents
  "DMS1000044", // Statutory Requirements / Power of Attorney
  "DMS1000045", // Shops & Establishments Act Certificate
  "DMS1000047", // Financial Ratios
  "DMS1000048", // Financial Evaluation
]);

// 3. Agreements
const AGREEMENT_DOC_IDS = new Set([
  "DMS1000021", // Platform SLA Agreement
  "DMS1000031", // Memorandum of Understanding (MOU)
  "DMS1000049", // Risk Disclosure Agreement
  "DMS1000050", // Transfer of Rights Agreement
  "DMS1000054", // Joint Venture NDA
  "DMS1000058", // Joint Venture SLA
]);

// 4. Deal Documents
const DEAL_DOC_IDS = new Set([
  "DMS1000057", // Deal Documents Upload
]);

// 5. Others
const OTHER_DOC_ID = "DMS1000046";

const getCategoryForDoc = (doc) => {
  const masterId = doc?.docMasterId;
  const name = (doc?.docMasterName || "").toLowerCase();

  // Deal Documents: specifically DMS1000057, invoice type, or deal-specific questionnaire uploads
  if (
    DEAL_DOC_IDS.has(masterId) ||
    doc?.docType === "INVOICE" ||
    (doc?.dealId && doc.dealId !== "unknown" && !KYC_DOC_MASTER_IDS.has(masterId) && masterId !== "DMS1000048")
  ) {
    return "DEAL";
  }

  // KYC Documents: docTypeId === 1, KYC ID, or KYC-related name
  if (
    doc?.docTypeId === 1 ||
    KYC_DOC_MASTER_IDS.has(masterId) ||
    name.includes("kyc") ||
    name.includes("pan") ||
    name.includes("aadhaar") ||
    name.includes("passport") ||
    name.includes("voter") ||
    name.includes("driving")
  ) {
    return "KYC";
  }

  // Agreements: Agreement ID, docTypeId === 2 (unless financial/other), or agreement-related name
  if (
    AGREEMENT_DOC_IDS.has(masterId) ||
    (doc?.docTypeId === 2 && !ONBOARDING_FINANCIAL_DOC_IDS.has(masterId) && masterId !== OTHER_DOC_ID) ||
    name.includes("agreement") ||
    name.includes("nda") ||
    name.includes("sla") ||
    name.includes("mou") ||
    name.includes("contract")
  ) {
    return "AGREEMENTS";
  }

  // Onboarding / Financial Documents
  if (
    ONBOARDING_FINANCIAL_DOC_IDS.has(masterId) ||
    name.includes("financial") ||
    name.includes("statement") ||
    name.includes("bank") ||
    name.includes("itr") ||
    name.includes("incorporation") ||
    name.includes("board resolution") ||
    name.includes("shareholding") ||
    name.includes("ratio") ||
    name.includes("sanction")
  ) {
    return "ONBOARDING_FINANCIAL";
  }

  // Others
  return "OTHERS";
};

const Documents = ({ orgId, org, dealCategoryId: propDealCategoryId }) => {
  const dispatch = useDispatch();
  const { docs, loading } = useSelector((state) => state.documents);

  const resolvedDealCategoryId = propDealCategoryId || org?.dealCategoryId;

  useEffect(() => {
    const payload = {
      type: "BUSINESS",
      orgId,
      dealCategoryId: resolvedDealCategoryId,
    };
    dispatch(fetchDocuments(payload));
  }, [dispatch, resolvedDealCategoryId, orgId]);

  const latestDocsByType = useMemo(() => {
    if (!Array.isArray(docs)) return [];

    const map = {};
    const transferOfRightsDocs = [];

    docs.forEach((doc) => {
      // Transfer of Rights → collect ALL
      if (doc.docMasterId === "DMS1000050") {
        transferOfRightsDocs.push(doc);
        return;
      }

      // Other documents → latest by modifiedAt
      const key = doc.docMasterId || doc.docId || doc.docUrl;
      const existing = map[key];

      if (
        !existing ||
        new Date(doc.modifiedAt) > new Date(existing.modifiedAt)
      ) {
        map[key] = doc;
      }
    });

    // Merge grouped docs + all transfer-of-rights docs
    return [...Object.values(map), ...transferOfRightsDocs];
  }, [docs]);

  const documentsToShow = useMemo(() => {
    // Joint Ventures
    if (resolvedDealCategoryId === "DCT1000002") {
      return latestDocsByType;
    }

    const existingIds = new Set(latestDocsByType.map((doc) => doc.docMasterId));

    const missingDocs = REQUIRED_FINANCIAL_DOCS.filter(
      (doc) => !existingIds.has(doc.docMasterId),
    ).map((doc) => ({
      ...doc,
      isMissing: true,
    }));

    return [...latestDocsByType, ...missingDocs];
  }, [latestDocsByType, resolvedDealCategoryId]);

  const { kycDocs, onboardingFinancialDocs, agreementDocs, dealDocs, otherDocs } = useMemo(() => {
    const kyc = [];
    const onboardingFinancial = [];
    const agreements = [];
    const deal = [];
    const others = [];

    documentsToShow.forEach((doc) => {
      const category = getCategoryForDoc(doc);
      if (category === "KYC") kyc.push(doc);
      else if (category === "ONBOARDING_FINANCIAL") onboardingFinancial.push(doc);
      else if (category === "AGREEMENTS") agreements.push(doc);
      else if (category === "DEAL") deal.push(doc);
      else others.push(doc);
    });

    return {
      kycDocs: kyc,
      onboardingFinancialDocs: onboardingFinancial,
      agreementDocs: agreements,
      dealDocs: deal,
      otherDocs: others,
    };
  }, [documentsToShow]);

  const renderSection = (title, desc, data, tagColor = "blue", sectionKey = "") => {
    if (!data || data.length === 0) return null;

    return (
      <div
        className="document-category-section"
        data-testid={sectionKey ? `section-${sectionKey}` : undefined}
        style={{ marginBottom: 24 }}
      >
        <Flex align="center" justify="space-between" style={{ marginBottom: 8 }}>
          <Space align="center" size={8}>
            <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
              {title}
            </Title>
            <Tag color={tagColor} style={{ borderRadius: 10, margin: 0 }}>
              {data.length}
            </Tag>
          </Space>
        </Flex>
        {desc && (
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 12 }}>
            {desc}
          </Text>
        )}

        <Row gutter={[16, 16]} align="stretch">
          {data.map((doc) => (
            <Col
              xs={24}
              md={12}
              key={doc.docId || doc.docMasterId || doc.docUrl}
            >
              <DocumentCard
                doc={doc}
                docType="BUSINESS"
                orgId={orgId}
                dealCategoryId={resolvedDealCategoryId}
              />
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const sectionsToRender = [
    {
      key: "kyc",
      title: "KYC Documents",
      desc: "Identity and registration proof (.pdf, .jpg, .jpeg, .png)",
      data: kycDocs,
      color: "blue",
    },
    {
      key: "onboardingFinancial",
      title: "Onboarding / Financial Documents",
      desc: "Financial statements, evaluations, and corporate records (.xlsx, .pdf, .zip)",
      data: onboardingFinancialDocs,
      color: "cyan",
    },
    {
      key: "agreements",
      title: "Agreements",
      desc: "Legal contracts, SLA, and signed agreements (.pdf)",
      data: agreementDocs,
      color: "purple",
    },
    {
      key: "deal",
      title: "Deal Documents",
      desc: "Deal proposals, pitch decks, and deal-specific uploads (.pdf, .xlsx, .zip, .doc, .docx)",
      data: dealDocs,
      color: "geekblue",
    },
    {
      key: "others",
      title: "Others",
      desc: DESC_TXT.UPLOAD_DOC_DESC + ".pdf, .zip",
      data: otherDocs,
      color: "default",
    },
  ].filter((section) => section.data && section.data.length > 0);

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 20 }}>
        {sectionsToRender.length === 0 ? (
          <Empty description="No Documents Found" />
        ) : (
          sectionsToRender.map((section, idx) => (
            <div key={section.key}>
              {renderSection(section.title, section.desc, section.data, section.color, section.key)}
              {idx < sectionsToRender.length - 1 && <Divider style={{ margin: "16px 0 24px" }} />}
            </div>
          ))
        )}
      </div>
    </Spin>
  );
};

export default Documents;
